import React, { MouseEventHandler, useState } from 'react'

import { Heading, ScrollList } from '@hhgtech/hhg-components'
import { Container as MantineContainer } from '@mantine/core'
import innertext from 'innertext'
import { useRouter } from 'next/router'

import { Container } from 'components/atoms/container'
import { AdBlock } from 'components/molecules/adBlock'
import { CollapsibleFaqs } from 'components/molecules/collapsibleFaqs'
import { ExpandedCard } from 'components/molecules/expandedCard'
import { GamingMechanismStepCard } from 'components/molecules/gamingMechanism'
import { SpotlightsExpert } from 'components/molecules/spotlightExpert'
import { SpotlightSubotList } from 'components/molecules/subotEmbedded'
import { WorkshopCard } from 'components/molecules/workshopCard'
import { WorkshopForm } from 'components/molecules/workshopForm'
import { ThankyouPopup } from 'components/molecules/workshopForm/thankyou'
import ClinicLocator from 'components/organisms/clinicLocator'
import { EmbeddedCommunity } from 'components/organisms/embededCommunity'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { VideoList } from 'components/organisms/videoLists'
import { ClickableBanner } from 'components/pages/landingPage/clickableBanner'
import { GeneralSection } from 'components/pages/landingPage/generalSection'
import {
  StyledGeneralSectionContainer,
  StyledLandingPageBanner,
  StyledLandingWrapper,
} from 'components/pages/landingPage/index.styled'
import { InformationSection } from 'components/pages/landingPage/infomationSection'
import { SpotlightList } from 'components/pages/landingPage/spotlightList'
// import { VideosSection } from 'components/pages/landingPage/videos'
import { getSpotlightLocaleAdMappingFromSlug } from 'interfaces/adCategoryMapping'
import { SHOW_WORKSHOP_THANK_YOU_ACTION } from 'interfaces/constants'
import { HealthTool, Spotlight, SpotlightExpert } from 'interfaces/types'
import { getAdTag, getCountryAdTag } from 'utils/ads'
import { fastdom } from 'utils/fastdom'

import {
  StyledGamingBanner,
  StyledGamingDescriptionWrapper,
  StyledGamingSection,
  StyledGamingSectionDescription,
  StyledGamingSectionTitle,
  StyledRewardContainer,
  StyledRewardImage,
  StyledRewardInfo,
  StyledStepCardWrapper,
  StyledWorkshopCardWrapper,
  StyledWorkshopContainer,
  StyledWorkshopSection,
  StyledWorkshopSectionDescription,
  StyledWorkshopSectionTitle,
} from './index.styled'

export type Props = {
  isMobile: boolean
  spotlight: Spotlight
  healthTools: HealthTool[]
}

const SpotlightLandingTemplate = ({ isMobile, spotlight }: Props) => {
  const { locale = 'en-US', query, replace, asPath } = useRouter()
  const [openWorkshopForm, setOpenWorkshopForm] = useState(false)
  const [openThankyouForm, setOpenThankyouForm] = useState(
    query?.action === SHOW_WORKSHOP_THANK_YOU_ACTION,
  )
  const [chosenWorkshop, setChosenWorkshop] = useState<Workshop | null>(null)
  const finalSlug = Array.isArray(query.slug)
    ? query.slug[query.slug.length - 1]
    : query.slug

  const [openInfo, setOpenInfo] = useState(-1)

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: {
      result:
        getSpotlightLocaleAdMappingFromSlug(locale, finalSlug || '') || '',
      redundant: true,
    },
  })

  const scrollToWorkshopSection: MouseEventHandler<HTMLAnchorElement> = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    e.preventDefault()
    fastdom.measure(() => {
      const element = document.getElementById('workshop')
      if (element) {
        const headerOffset = isMobile ? 0 : 120
        const offsetPosition =
          element.getBoundingClientRect().top + window.scrollY - headerOffset

        window.scrollTo({
          top: offsetPosition,
          behavior: 'auto',
        })
      }
    })
  }
  const isHavingSubot = spotlight?.subot && spotlight?.subot?.post?.length > 0
  const isHavingExpertsList =
    spotlight?.expert && spotlight?.expert?.experts?.length > 0
  const isHavingFaqs = spotlight?.faqs && spotlight?.faqs.length > 0
  const workshop = spotlight?.workshop || null
  const gaming = spotlight?.gaming || null
  const gameBannerMobile = gaming?.game.mobileBanner
  const gameBannerDesktop = gaming?.game.desktopBanner
  const informationBlockColumns = isMobile ? 2 : 4
  const slideToShow = isMobile ? 1.2 : 2.5

  const customTargetings = {
    Is_Sponsored: [1],
    Is_Expert: [spotlight?.expert?.experts?.[0]?.id ? 1 : 0],
    Is_Affiliate: [0],
    Brand: spotlight?.sponsor?.map((s) => innertext(s.title || '')) || [],
    // [spotlight?.hasAffiliate ? 1 : 0],
  }

  return (
    <StyledLandingWrapper>
      <WorkshopForm
        isOpen={openWorkshopForm}
        onClose={() => {
          setChosenWorkshop(null)
          setOpenWorkshopForm(false)
        }}
        workshops={(workshop?.sections || []).filter(
          (_workshop) => _workshop.active,
        )}
        chosenWorkshop={chosenWorkshop}
        isMobile={isMobile}
        onSuccess={() => {
          setChosenWorkshop(null)
          setOpenWorkshopForm(false)
          setOpenThankyouForm(true)
        }}
      />
      <ThankyouPopup
        isMobile={isMobile}
        isOpen={openThankyouForm}
        onClose={() => {
          setOpenThankyouForm(false)
          replace(
            asPath.replace(`action=${SHOW_WORKSHOP_THANK_YOU_ACTION}`, ''),
            undefined,
            { shallow: true },
          )
        }}
      />
      {spotlight?.banner && (
        <StyledLandingPageBanner
          banner={spotlight?.banner}
          onClick={scrollToWorkshopSection}
        />
      )}
      <SpotlightList
        spotLights={spotlight.posts.map((post) => ({
          name: post.title,
        }))}
        isMobile={isMobile}
        isSponsored={spotlight?.isSponsor}
        applyH1
      />
      {spotlight?.clickable_banners &&
        spotlight.clickable_banners.list_banners &&
        spotlight.clickable_banners.list_banners.length > 0 && (
          <ClickableBanner
            banner={{
              ...spotlight.clickable_banners,
              listing: [
                {
                  banners: spotlight.clickable_banners.list_banners,
                },
              ],
            }}
          />
        )}
      {(isHavingSubot || isHavingExpertsList || isHavingFaqs) && (
        <Container
          style={{
            maxWidth: '1170px',
            marginTop: isMobile ? 24 : 64,
            marginBottom: isMobile ? 24 : 64,
            padding: '0 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 60 : 80,
          }}
        >
          {isHavingSubot && (
            <SpotlightSubotList
              isMobile={isMobile}
              spotlightSubot={spotlight?.subot}
            />
          )}
          {isHavingExpertsList && (
            <div>
              <Heading tag="h3" style={{ margin: '0 0 32px 0' }}>
                {spotlight?.expert?.title}
              </Heading>
              {spotlight?.expert?.experts?.length > 0 && (
                <ScrollList
                  row={1}
                  column={3}
                  isDotsShown={true}
                  isArrowShown={!isMobile}
                  mode="snapped"
                >
                  {spotlight?.expert?.experts.map(
                    (expert: SpotlightExpert, index: number) => (
                      <SpotlightsExpert
                        key={index}
                        isMobile={isMobile}
                        expertInfo={expert}
                        isSingleExpert={
                          spotlight?.expert?.experts?.length === 1
                        }
                      />
                    ),
                  )}
                </ScrollList>
              )}
            </div>
          )}
          {isHavingFaqs && (
            <CollapsibleFaqs
              isMobile={isMobile}
              posts={spotlight?.faqs}
              title={spotlight?.faqsTitle}
            />
          )}
        </Container>
      )}
      <StyledGeneralSectionContainer>
        {spotlight.posts.length > 0 && (
          <>
            <GeneralSection
              title={spotlight.posts[0].title}
              posts={spotlight.posts[0].post}
            />
          </>
        )}
      </StyledGeneralSectionContainer>
      {process.env.NEXT_PUBLIC_DEPLOY_ENV !== 'production' &&
      (spotlight.postName === 'la-phu-nu-chon-yeu-minh' ||
        spotlight.postName ===
          '%e0%b8%a7%e0%b8%b1%e0%b8%84%e0%b8%8b%e0%b8%b5%e0%b8%99-hpv') ? (
        <ClinicLocator />
      ) : null}
      {!spotlight.disableAd && (
        <AdBlock
          adUnitPath={adUnitPath}
          size={[
            [300, 250],
            [300, 600],
          ]}
          divId={'div-gpt-ad-home-sidebar1'}
          pos={['MidBanner1']}
          style={{
            marginBlock: '1rem',
            maxWidth: '100%',
          }}
          customTargetings={customTargetings}
        />
      )}
      {!!workshop &&
        (!!workshop.title ||
          !!workshop.description ||
          !!workshop.sections?.length) && (
          <StyledWorkshopSection id="workshop">
            <StyledWorkshopContainer>
              {!!workshop.title && (
                <StyledWorkshopSectionTitle>
                  {workshop.title}
                </StyledWorkshopSectionTitle>
              )}
              {!!workshop.description && (
                <div>
                  <StyledWorkshopSectionDescription
                    dangerouslySetInnerHTML={{
                      __html: workshop.description || '',
                    }}
                  ></StyledWorkshopSectionDescription>
                </div>
              )}
              {!!workshop.sections?.length && (
                <StyledWorkshopCardWrapper>
                  {workshop.sections?.map((_workshop, index) => (
                    <WorkshopCard
                      key={index}
                      workshop={_workshop}
                      onSubscribe={() => {
                        setChosenWorkshop(_workshop)
                        setOpenWorkshopForm(true)
                      }}
                    />
                  ))}
                </StyledWorkshopCardWrapper>
              )}
            </StyledWorkshopContainer>
          </StyledWorkshopSection>
        )}
      {!!gaming && (
        <StyledGamingSection>
          {(!!gameBannerDesktop || (isMobile && !!gameBannerMobile)) && (
            <StyledGamingBanner href={gaming?.game?.url} target="_blank">
              <img
                src={
                  isMobile
                    ? gameBannerMobile || gameBannerDesktop
                    : gameBannerDesktop
                }
                alt="gaming"
                loading="lazy"
              />
            </StyledGamingBanner>
          )}
          {!!gaming.mechanism && (
            <>
              {!!gaming.mechanism.title && (
                <StyledGamingSectionTitle>
                  {gaming?.mechanism.title}
                </StyledGamingSectionTitle>
              )}
              {!!gaming.mechanism.description && (
                <StyledGamingDescriptionWrapper>
                  <StyledGamingSectionDescription
                    dangerouslySetInnerHTML={{
                      __html: gaming.mechanism.description || '',
                    }}
                  ></StyledGamingSectionDescription>
                </StyledGamingDescriptionWrapper>
              )}
              {!!gaming.mechanism.steps?.length && (
                <StyledStepCardWrapper>
                  {gaming.mechanism.steps.map((steps, index) => (
                    <GamingMechanismStepCard key={index} steps={steps} />
                  ))}
                </StyledStepCardWrapper>
              )}
            </>
          )}
          {gaming.reward &&
          (gaming.reward.image || gaming.reward.items?.length) ? (
            <StyledRewardContainer>
              {!!gaming.reward.image && (
                <StyledRewardImage>
                  <img src={gaming.reward.image} alt="" loading="lazy" />
                </StyledRewardImage>
              )}
              {!!gaming.reward.items?.length && (
                <StyledRewardInfo>
                  {gaming.reward.items.map((item, iItem) => (
                    <ExpandedCard
                      key={'info-' + iItem}
                      isExpand={openInfo === iItem}
                      onChange={(value: boolean) =>
                        value ? setOpenInfo(iItem) : setOpenInfo(-1)
                      }
                      title={item.title}
                      contentString={item.description}
                      iconPosition="right"
                      isMobile={isMobile}
                    />
                  ))}
                </StyledRewardInfo>
              )}
            </StyledRewardContainer>
          ) : null}
        </StyledGamingSection>
      )}
      <Container
        style={{
          marginTop: 50,
        }}
      >
        {spotlight?.community && (
          <EmbeddedCommunity
            key={`${isMobile}`}
            columns={isMobile ? 1 : 3}
            isMobile={isMobile}
            communityId={spotlight?.community?.id}
            titleMember={spotlight?.communityTitle}
            titleGuest={spotlight?.communityTitle}
            descriptionMember={spotlight?.community?.memberDesc}
            descriptionGuest={spotlight?.community?.guestDesc}
            showSeparatedLine={false}
            isSpotlightLandingPage={true}
          />
        )}
      </Container>
      {spotlight.posts.slice(1, spotlight.posts.length).map((item, index) => (
        <div key={index}>
          {item.typeView === 'advance' ? (
            <>
              <GeneralSection title={item.title} posts={item.post} />
              {item.enableVideo && spotlight.videos?.videos && (
                <MantineContainer size="lg">
                  <VideoList
                    videos={spotlight.videos.videos.map((url) => ({
                      videoUrl: url.video,
                    }))}
                    slideToShow={slideToShow}
                  />
                </MantineContainer>
                // <VideosSection
                //   isMobile={isMobile}
                //   videoData={spotlight.videos}
                // />
              )}
            </>
          ) : (
            <>
              <InformationSection
                posts={item.post}
                title={item.title}
                viewType={item.typeView}
                isMobile={isMobile}
                columns={informationBlockColumns}
              />
              {item.enableVideo && spotlight.videos?.videos && (
                <MantineContainer size="lg">
                  <VideoList
                    videos={spotlight.videos.videos.map((url) => ({
                      videoUrl: url.video,
                    }))}
                    slideToShow={slideToShow}
                  />
                </MantineContainer>
                // <VideosSection
                //   isMobile={isMobile}
                //   videoData={spotlight.videos}
                // />
              )}
            </>
          )}
        </div>
      ))}
      {spotlight.videos?.videos && (
        <MantineContainer size="lg">
          <VideoList
            videos={spotlight.videos.videos.map((url) => ({
              videoUrl: url.video,
            }))}
            slideToShow={slideToShow}
          />
        </MantineContainer>
        // <VideosSection isMobile={isMobile} videoData={spotlight.videos} />
      )}
      <MobileBottomNavigationWrapper />
    </StyledLandingWrapper>
  )
}

export default SpotlightLandingTemplate
